/* eslint-disable @typescript-eslint/no-unnecessary-condition --
   Proto types map/repeated fields as required but proto3 JSON OMITS empty ones; guards are deliberate. */
/**
 * BUNDLE_GALLERY_V2 media resolution.
 *
 * A bundle gallery is scoped ONLY by each asset's inline `vdas` map (`Record<vdaKey, {values}>`) — unlike a
 * normal-product V2 gallery there are no variant media refs to join on (the proto omits `variants` /
 * `hydratedVariants` entirely). Contract: an asset applies when the current selection's value for EVERY key
 * present on the asset is in that key's `values`; an empty map means the asset always applies.
 *
 * Two deliberate softenings of that rule, both decided 2026-08-13 (D5 / D6 in `master-plan.md`):
 *
 * 1. A key the asset scopes on but the selection does not carry is treated as NON-constraining rather than
 *    as a miss. The selection published for a bundle is the first required child's anchor subset, so a
 *    (hypothetical) size-scoped asset would otherwise be unmatchable and silently drop the whole gallery.
 * 2. When nothing matches, fall back to global (unscoped) assets, and if there are none, to ALL assets, so
 *    the gallery is never empty. Today's data makes this the common path, not an edge case: the captured
 *    bundle tags its 13 assets across 3 colours while the constituents carry up to 49, so the server's own
 *    default selection (Moss) matches zero assets.
 */

import { MediaType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import Image from '@src/quince-site-common/src/domains/Image'
import ProductImage from '@src/quince-site-common/src/domains/ProductImage'
import type DomainProductMedia from '@src/quince-site-common/src/domains/ProductMedia'
import ProductVideo from '@src/quince-site-common/src/domains/ProductVideo'
import Video from '@src/quince-site-common/src/domains/Video'
import { VIDEO_FORCE_POSITION } from '@src/quince-site-common/src/utils/constants'
import { isProductVideo } from '@src/quince-site-common/src/utils/typeGuards'

import type {
  MediaAsset,
  MediaContent,
  ProductGalleryFlags,
  ProductMedia,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

// eslint-disable-next-line i18next/no-literal-string -- MIME type fallback, not user-facing copy.
const DEFAULT_VIDEO_CONTENT_TYPE = 'video/mp4'
/** Non-PL bundle galleries hard-cap at 10 (`bundle-product-images-legacy.tsx:49`); V2 keeps that. */
const BUNDLE_GALLERY_IMAGE_LIMIT = 10

/** Assets in stable map order, so the gallery order is the server's order. */
function allAssets(media: ProductMedia | undefined): MediaAsset[] {
  return Object.values(media?.mediaAssets ?? {})
}

/** Whether an asset carries no VDA scoping at all — a "global" asset per the contract. */
function isGlobalAsset(asset: MediaAsset): boolean {
  return Object.keys(asset.vdas ?? {}).length === 0
}

/**
 * Does this asset apply to `selection`? Every scoped key the selection HAS a value for must match; keys the
 * selection doesn't carry are ignored (softening 1 above).
 */
function assetMatchesSelection(asset: MediaAsset, selection: Record<string, string>): boolean {
  return Object.entries(asset.vdas ?? {}).every(([vdaKey, matchValues]) => {
    const selectedValue = selection[vdaKey]
    if (selectedValue === undefined) return true
    return (matchValues?.values ?? []).includes(selectedValue)
  })
}

/**
 * The assets to render for the current selection, applying the fallback chain: scoped matches → global-only
 * → everything. Returns assets in server order; the caller converts them to domain media.
 */
export function resolveBundleGalleryAssets(
  media: ProductMedia | undefined,
  selection: Record<string, string>,
): MediaAsset[] {
  const assets = allAssets(media)
  if (assets.length === 0) return []

  const matched = assets.filter(asset => assetMatchesSelection(asset, selection))
  // A selection-scoped match set that is only the global assets is still a legitimate result; the fallbacks
  // below matter when the selection excludes everything scoped AND nothing is global.
  if (matched.length > 0) return matched

  const global = assets.filter(isGlobalAsset)
  return global.length > 0 ? global : assets
}

/** Contentful asset URLs arrive protocol-relative (`//images.ctfassets.net/…`). */
function withScheme(url: string): string {
  return url.startsWith('//') ? `https:${url}` : url
}

/** The legacy Video atom prepends `https:` itself, so it must be fed a protocol-relative URL. */
function protocolRelative(url: string): string {
  return url.replace(/^https?:/, '')
}

function toImage(content: MediaContent, index: number, toUrl: (url: string) => string = withScheme): Image {
  return new Image({
    id: content.id ?? `bundle-gallery-media-${index}`,
    url: toUrl(content.url ?? ''),
    fileName: content.fileName ?? `bundle-gallery-media-${index}`,
    height: content.height ?? 0,
    width: content.width ?? 0,
    alt: content.alt ?? content.title ?? '',
    title: content.title ?? '',
    description: content.description ?? '',
    // Bundle assets carry no model-size tagging (the non-PL bundle gallery shows none either).
    displayModelInfo: false,
  })
}

/** One resolved asset → domain media. Bundle assets carry no `options`: scoping is the `vdas` map. */
function toDomainMedia(asset: MediaAsset, index: number): DomainProductMedia | null {
  const content = asset.media?.content
  if (!content?.url) return null

  if (asset.media?.type === MediaType.VIDEO) {
    const video = new Video({
      id: content.id ?? `bundle-gallery-video-${index}`,
      url: protocolRelative(content.url),
      fileName: content.fileName ?? `bundle-gallery-video-${index}`,
      contentType: content.contentType ?? DEFAULT_VIDEO_CONTENT_TYPE,
      title: content.title ?? '',
      description: content.description ?? '',
    })
    const thumbnail = content.thumbnail
    const videoPoster = thumbnail?.url ? toImage(thumbnail, index, protocolRelative) : undefined
    return new ProductVideo({ video, options: [], videoPoster })
  }

  return new ProductImage({ image: toImage(content, index), options: [] })
}

/**
 * The gallery's render list for the current selection: resolved assets → domain media, images capped at the
 * non-PL bundle limit, with the video slotted at `flags.forcedVideoPosition` (falling back to the shared
 * `VIDEO_FORCE_POSITION`) exactly as the V1 gallery does.
 */
export function buildBundleGalleryMedia(
  media: ProductMedia | undefined,
  selection: Record<string, string>,
  flags?: ProductGalleryFlags,
): DomainProductMedia[] {
  const resolved = resolveBundleGalleryAssets(media, selection)
    .map((asset, index) => toDomainMedia(asset, index))
    .filter((item): item is DomainProductMedia => item !== null)

  // `isProductVideo`, not `instanceof`: the media domain constructors `return {...this}`, so every
  // instance is a plain object and `instanceof ProductVideo` is ALWAYS false (the shared gallery helpers
  // use the same guard for the same reason).
  const galleryMedia: DomainProductMedia[] = resolved
    .filter(item => !isProductVideo(item))
    .slice(0, BUNDLE_GALLERY_IMAGE_LIMIT)
  const video = resolved.find(item => isProductVideo(item))
  if (!video) return galleryMedia

  const flagPosition = flags?.forcedVideoPosition
  const forcedVideoPosition = flagPosition && flagPosition > 0 ? flagPosition : VIDEO_FORCE_POSITION
  const videoIndex = Math.min(forcedVideoPosition - 1, galleryMedia.length)
  galleryMedia.splice(videoIndex, 0, video)
  return galleryMedia
}

/* eslint-enable @typescript-eslint/no-unnecessary-condition */
